// ═══════════════════════════════════════════════════════════════════════════════════════════
// ClalStatusChip — the ONLY component allowed to render `eClalItemStatus`.
//
// ⚠️ WHY THIS EXISTS AND WHY `StatusChip` MAY NOT BE REUSED (C6 v12 §4, 14-W3 "תוצרים"):
// `screens/DataSources/components/StatusChip.tsx` takes `eDataSourceStatus`
// (PENDING=0 · PROCESSING=1 · READY=2 · FAIL=3 · CANCELLED=4). TypeScript accepts a numeric
// literal wherever a numeric enum is expected, so passing a ClalCenter status COMPILES CLEANLY —
// and then paints "טיוטה"(0) as "ממתין", "פורסם"(1) as "בעיבוד" and "מוסתר"(2) as "מוכן" in
// green. A silent, type-checked lie. Hence a separate chip with its own enum.
//
// Colours are chosen EXPLICITLY here. The palette quoted in `04-EXPERTS\E3-admin-ux.md` is only
// partly real: `#eef1f4` does NOT exist anywhere in `src` (verified), so nothing is "reused" —
// the three pairs below are declared and owned by this file.
// ═══════════════════════════════════════════════════════════════════════════════════════════

import React from 'react';
import { Box, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { CC, eClalItemStatus } from '../../../Models/ClalCenter/ClalCenter';

const PALETTE: { [status: number]: { bg: string; fg: string } } = {
    // Ratios recomputed 07.09.2026 (WCAG 2.x). The two that failed AA are raised; PUBLISHED
    // already passed at 5.02 and is left alone so the three chips keep their hue relationship.
    [eClalItemStatus.DRAFT]: { bg: 'var(--pls-d-warning-bg, #fff8e1)', fg: 'var(--pls-d-warning, #8A5A00)' },      // was #b7791f = 3.43 FAIL -> 5.58
    [eClalItemStatus.PUBLISHED]: { bg: 'var(--pls-d-success-bg, #e6f4ec)', fg: 'var(--pls-d-success, #067647)' },  // 5.02, unchanged
    [eClalItemStatus.HIDDEN]: { bg: 'var(--pls-d-surface-2, #eef1f4)', fg: 'var(--pls-d-text-2, #5A6775)' }      // was #7a8794 = 3.24 FAIL -> 5.10
};

/**
 * The accent stripe a row gets on its inline-start edge. `null` ⇒ no stripe (published rows).
 *
 * THESE TWO HEXES ARE DELIBERATELY NOT the ones the text sites use, and #b7791f in particular
 * survived the 08.09.2026 sweep that raised it to #8A5A00 in all four `color:` positions on this
 * screen. A stripe is NON-TEXT, so its floor is SC 1.4.11's 3:1, not SC 1.4.3's 4.5:1 — and
 * #b7791f on white measures 3.64, which clears it. Darkening it to #8A5A00 (5.93) would have made
 * a 3px decorative edge darker than the label beside it, for no requirement.
 *
 * #b6c0c9 on white is 1.85 and clears NOTHING. It is left as it is on purpose: the stripe is a
 * REDUNDANT affordance, never the only carrier of the state — every row that gets one also
 * renders <ClalStatusChip>, which says "מוסתר" or "טיוטה" in words at 5.10 and 5.58. SC 1.4.11
 * governs "visual information required to identify" a component; nothing here is required,
 * because the words are always present. If a future change ever removes the chip from a row that
 * keeps the stripe, that reasoning dies with it and both hexes need re-measuring.
 */
export const rowStripeColor = (status: eClalItemStatus): string | null => {
    if (status === eClalItemStatus.DRAFT) return '#b7791f';   // 3.64 on white — non-text, passes
    if (status === eClalItemStatus.HIDDEN) return '#b6c0c9';  // 1.85 — decorative only, see above
    return null;
};

interface Props {
    status: eClalItemStatus;
    /**
     * `Status === PUBLISHED && UpdatedDate > Config.LastPublishedOn` — the item is live but what
     * is live is not what is on screen. Rendered as a suffix, never as a fourth status.
     */
    pendingChanges?: boolean;
}

const ClalStatusChip = ({ status, pendingChanges = false }: Props) => {
    const { t } = useTranslation();
    const colors = PALETTE[status] ?? PALETTE[eClalItemStatus.DRAFT];
    const label = t(`${CC}status.${status}`);

    return (
        <Box
            role="status"
            component="span"
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                background: colors.bg,
                color: colors.fg,
                borderRadius: 999,
                padding: '2px 11px',
                fontSize: 12.5,
                fontWeight: 600,
                whiteSpace: 'nowrap'
            }}
        >
            <Typography component="span" style={{ fontSize: 12.5, fontWeight: 600, color: 'inherit' }}>
                {label}
            </Typography>
            {pendingChanges && (
                <Typography
                    component="span"
                    style={{ fontSize: 12, fontWeight: 600, color: 'var(--pls-d-warning, #8A5A00)' }}
                >
                    {'· ' + t(`${CC}status.pendingChanges`)}
                </Typography>
            )}
        </Box>
    );
};

export default ClalStatusChip;
